import React, { useEffect, useRef, useState } from "react";
import { SERVICES } from "../data/mock";
import { Check } from "lucide-react";
import Reveal from "./Reveal";

const AUTO_MS = 4500;

export default function Services() {
  const [activeIdx, setActiveIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  const [inView, setInView] = useState(false);

  const startRef = useRef(performance.now());
  const pausedRef = useRef(false);
  const rafRef = useRef(null);
  const sectionRef = useRef(null);

  // IntersectionObserver to retrigger from tab 0 every scroll-in
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setActiveIdx(0);
            setProgress(0);
            startRef.current = performance.now();
            setInView(true);
          } else {
            setInView(false);
          }
        });
      },
      { threshold: 0.25 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Animation loop: drives progress + auto-advance only when in view
  useEffect(() => {
    if (!inView) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      return undefined;
    }
    startRef.current = performance.now();
    const tick = (now) => {
      if (!pausedRef.current) {
        const elapsed = now - startRef.current;
        const pct = Math.min((elapsed / AUTO_MS) * 100, 100);
        setProgress(pct);
        if (elapsed >= AUTO_MS) {
          setActiveIdx((i) => (i + 1) % SERVICES.length);
          startRef.current = now;
          setProgress(0);
        }
      } else {
        startRef.current = now - (progress / 100) * AUTO_MS;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIdx, inView]);

  const selectTab = (i) => {
    setActiveIdx(i);
    startRef.current = performance.now();
    setProgress(0);
  };

  const current = SERVICES[activeIdx];

  return (
    <section className="section" id="services" ref={sectionRef}>
      <div className="container-x">
        <Reveal>
          <div className="text-center mb-12">
            <div className="font-script" style={{ fontSize: 22, color: "var(--agero-orange)" }}>
              (How It Works)
            </div>
            <h2
              className="font-display mt-3"
              style={{
                fontSize: "clamp(40px, 6vw, 92px)",
                letterSpacing: "-0.05em",
                lineHeight: 0.98,
              }}
            >
              The 4-Part LinkedIn
              <br />
              <span className="gradient-orange-text">Revenue System</span>
            </h2>
          </div>
        </Reveal>

        {/* Tab progress strip */}
        <div
          className="relative mx-auto mb-8"
          style={{ maxWidth: 920 }}
          onMouseEnter={() => (pausedRef.current = true)}
          onMouseLeave={() => (pausedRef.current = false)}
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {SERVICES.map((s, i) => {
              const isActive = i === activeIdx;
              return (
                <button
                  key={s.key}
                  onClick={() => selectTab(i)}
                  className="text-left p-4 rounded-2xl bg-white border border-neutral-200/60 transition-all relative overflow-hidden"
                  style={{
                    boxShadow: isActive ? "0 8px 24px rgba(255,51,1,0.18)" : "none",
                    transform: isActive ? "translateY(-2px)" : "translateY(0)",
                  }}
                >
                  <div className="flex items-center justify-between">
                    <span
                      className="text-[11px] font-semibold tracking-wider"
                      style={{
                        color: isActive ? "var(--agero-orange)" : "#9a9a9a",
                      }}
                    >
                      0{i + 1}
                    </span>
                    {isActive && (
                      <span
                        className="w-1.5 h-1.5 rounded-full"
                        style={{ background: "var(--agero-orange)" }}
                      />
                    )}
                  </div>
                  <div
                    className="mt-2 font-semibold text-[15px]"
                    style={{ color: isActive ? "#0A0A0A" : "#525252" }}
                  >
                    {s.title}
                  </div>

                  <div
                    className="absolute left-0 right-0 bottom-0 h-[3px]"
                    style={{ background: "rgba(0,0,0,0.06)" }}
                  >
                    <div
                      className="h-full"
                      style={{
                        width: isActive ? `${progress}%` : i < activeIdx ? "100%" : "0%",
                        background: "var(--agero-orange)",
                        transition: isActive ? "none" : "width 0.4s ease",
                      }}
                    />
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div
          className="grid lg:grid-cols-2 gap-8 items-stretch"
          onMouseEnter={() => (pausedRef.current = true)}
          onMouseLeave={() => (pausedRef.current = false)}
        >
          <div
            className="rounded-[32px] overflow-hidden relative flex items-center justify-center"
            style={{
              background:
                "linear-gradient(135deg, #0A0A0A 0%, #2a0a02 60%, #ff3301 140%)",
              minHeight: 420,
            }}
            key={`visual-${activeIdx}`}
          >
            <div
              className="absolute inset-0 opacity-30"
              style={{
                background:
                  "radial-gradient(80% 60% at 50% 50%, rgba(255,51,1,0.5) 0%, rgba(255,51,1,0) 70%)",
              }}
            />
            <div
              className="relative text-center px-8 fade-up"
              style={{ animation: "fadeUp 0.6s ease-out" }}
            >
              <div
                className="font-display"
                style={{
                  fontSize: "clamp(80px, 10vw, 180px)",
                  color: "#fff",
                  lineHeight: 0.9,
                  letterSpacing: "-0.05em",
                  opacity: 0.95,
                }}
              >
                0{activeIdx + 1}
              </div>
              <div
                className="font-display mt-2"
                style={{
                  fontSize: "clamp(24px, 2.4vw, 36px)",
                  color: "#fff",
                  letterSpacing: "-0.03em",
                }}
              >
                {current.title}
              </div>
            </div>
          </div>

          <div
            key={`text-${activeIdx}`}
            className="flex flex-col justify-between"
            style={{ animation: "fadeUp 0.5s ease-out" }}
          >
            <div>
              <h3
                className="font-display"
                style={{
                  fontSize: "clamp(32px, 3.6vw, 52px)",
                  letterSpacing: "-0.03em",
                  lineHeight: 1.02,
                }}
              >
                {current.title}
              </h3>
              <p className="mt-6 text-neutral-700 text-[17px] leading-relaxed max-w-xl">
                {current.description}
              </p>
            </div>
            <div className="mt-8 flex flex-wrap gap-2.5">
              {current.tags.map((t) => (
                <div
                  key={t}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-white text-neutral-800 border border-neutral-200/70"
                >
                  <Check size={14} style={{ color: "var(--agero-orange)" }} />
                  <span className="font-medium text-[14px]">{t}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-20 marquee-container">
          <div
            className="flex items-center gap-6 py-4 text-white"
            style={{ background: "var(--agero-orange)" }}
          >
            <div className="marquee">
              {Array.from({ length: 18 }).map((_, i) => (
                <div
                  key={i}
                  className="flex items-center gap-6 px-8 text-[22px] font-semibold whitespace-nowrap"
                >
                  <span>Content &rarr; Conversion &rarr; Pipeline</span>
                  <span>&#10033;</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
